import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService.js';
import OTPInput from './OTPInput.jsx';
import PasswordStrengthIndicator from '../common/PasswordStrengthIndicator.jsx';

const Register = () => {
  const [step, setStep] = useState(1); // 1: email, 2: otp, 3: details
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registerStep, setRegisterStep] = useState('form'); // 'form', 'processing', 'success'
  const [securityProgress, setSecurityProgress] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [nameValidation, setNameValidation] = useState({ isValid: true, error: '' });
  
  const navigate = useNavigate();

  const validateName = (name) => {
    // Allow letters, spaces, hyphens, apostrophes, and dots
    const validNamePattern = /^[a-zA-Z\s\-'.]+$/;
    
    if (!name) {
      return { isValid: true, error: '' }; // Empty is OK, required validation handles this
    }
    
    if (!validNamePattern.test(name)) {
      return { 
        isValid: false, 
        error: 'Name can only contain letters, spaces, hyphens, apostrophes, and dots' 
      };
    }
    
    if (name.length > 50) {
      return { 
        isValid: false, 
        error: 'Name must be less than 50 characters' 
      };
    }
    
    return { isValid: true, error: '' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Real-time validation for name field
    if (name === 'name') {
      setNameValidation(validateName(value));
    }
    
    setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const simulateRegistrationSecurity = () => {
    const checks = [
      { id: 1, text: 'Encrypting user data...', delay: 500 },
      { id: 2, text: 'Generating secure tokens...', delay: 1000 },
      { id: 3, text: 'Creating secure profile...', delay: 1500 },
      { id: 4, text: 'Setting up security protocols...', delay: 2000 },
      { id: 5, text: 'Account created successfully!', delay: 2500 }
    ];

    setSecurityProgress([]);
    checks.forEach((check, index) => {
      setTimeout(() => {
        setSecurityProgress(prev => [...prev, check]);
        if (index === checks.length - 1) {
          setTimeout(() => setRegisterStep('success'), 300);
        }
      }, check.delay);
    });
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.sendOTP(formData.email, 'register');
      setStep(2);
    } catch (error) {
      setError(error.error || error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otp) => {
    setLoading(true);
    setError('');

    try {
      await authService.verifyOTP(otp);
      setStep(3);
    } catch (error) {
      setError(error.error || error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setRegisterStep('processing'); // Set to processing but don't start animation yet

    // Validate name before proceeding
    if (!nameValidation.isValid) {
      setError('Please fix the name field before proceeding.');
      setLoading(false);
      setRegisterStep('form');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      setRegisterStep('form');
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError('Password requirements not met:\n' + passwordErrors.map(err => `• ${err}`).join('\n'));
      setLoading(false);
      setRegisterStep('form');
      return;
    }

    try {
      // First try the API call
      const response = await authService.register({
        name: formData.name,
        password: formData.password
      });
      
      // Only start the animation if registration is successful
      simulateRegistrationSecurity();
      
      // Based on your API, registration doesn't return JWT
      // User needs to login after registration
      setSuccess('Registration successful! Please login with your credentials.');
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed. Please try again.';
      
      // Handle specific error messages
      if (error.response?.status === 400) {
        if (error.response.data?.error?.includes('password')) {
          errorMessage = 'Password does not meet security requirements. Please ensure your password is at least 8 characters long and contains uppercase, lowercase, numbers, and special characters.';
        } else if (error.response.data?.message?.includes('password')) {
          errorMessage = 'Password does not meet security requirements. Please ensure your password is at least 8 characters long and contains uppercase, lowercase, numbers, and special characters.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 409) {
        errorMessage = 'Email address is already registered. Please use a different email or try logging in.';
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setRegisterStep('form');
      setSecurityProgress([]);
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={handleSendOTP}>
            <div className="form-group">
              <label htmlFor="email">Email Address:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </div>

            <button 
              type="submit" 
              className={`btn btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-shield-alt"></i>
                  Securing Email...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Send OTP
                </>
              )}
            </button>
          </form>
        );

      case 2:
        return (
          <div className="verify-step">
            
            <div className="alert alert-info">
              <i className="fas fa-shield-alt"></i>
              🔐 Verify Your Email.Enter the 6-digit OTP sent to {formData.email}
            </div>
            
            <OTPInput 
              onComplete={handleVerifyOTP}
              disabled={loading}
            />
            {loading && (
              <div className="loading">
                
                Verifying secure code...
              </div>
            )}
            <button 
              type="button" 
              className="clear-btn"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Change Email Address
            </button>
          </div>
        );

      case 3:
        return (
          <form onSubmit={handleRegister}>
            <div className="form-group secure">
              <label htmlFor="name">Full Name:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
                disabled={loading}
                className={!nameValidation.isValid ? 'input-invalid' : ''}
                style={{
                  borderColor: !nameValidation.isValid ? '#ff4757' : undefined,
                  boxShadow: !nameValidation.isValid ? '0 0 0 2px rgba(255, 71, 87, 0.2)' : undefined
                }}
              />
              {!nameValidation.isValid && (
                <div className="validation-error" style={{
                  color: '#ff4757',
                  fontSize: '0.85rem',
                  marginTop: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  {nameValidation.error}
                </div>
              )}
            </div>

            <div className="form-group secure">
              <label htmlFor="password">Password:</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              <PasswordStrengthIndicator password={formData.password} />
            </div>

            <div className="form-group secure">
              <label htmlFor="confirmPassword">Confirm Password:</label>
              <div className="password-input-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={toggleConfirmPasswordVisibility}
                  disabled={loading}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>


            <button 
              type="submit" 
              className={`btn btn-primary auth-submit-btn ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-shield-alt"></i>
                  Creating Secure Account...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus"></i>
                  Complete Registration
                </>
              )}
            </button>
          </form>
        );

      default:
        return null;
    }
  };

  const renderProcessing = () => (
    <div className="auth-loading-container">
      <div className="security-icons-ring">
        <div className="security-icon"><i className="fas fa-user-shield"></i></div>
        <div className="security-icon"><i className="fas fa-lock"></i></div>
        <div className="security-icon"><i className="fas fa-key"></i></div>
        <div className="security-icon"><i className="fas fa-shield-alt"></i></div>
        <div className="security-icon"><i className="fas fa-fingerprint"></i></div>
        <div className="security-icon"><i className="fas fa-certificate"></i></div>
        <div className="central-security-logo">
          <i className="fas fa-user-plus"></i>
        </div>
      </div>
      
      <div className="auth-loading-text">Creating Your Secure Account</div>
      <div className="auth-loading-subtext">
        Your account is being created with enterprise-grade security protocols
      </div>

      <div className="security-progress">
        {securityProgress.map((item, index) => (
          <div key={item.id} className="security-progress-item completed">
            <i className="fas fa-check-circle"></i>
            <span>{item.text}</span>
          </div>
        ))}
        {securityProgress.length < 5 && (
          <div className="security-progress-item active">
            <i className="fas fa-spinner"></i>
            <span>Processing...</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderRegistrationSuccess = () => (
    <div className="auth-success-container">
      <div className="auth-success-icon">
        <i className="fas fa-user-check"></i>
      </div>
      <div className="auth-success-text">Account Created Successfully!</div>
      <div className="auth-success-subtext">Your secure account is ready. Redirecting to login...</div>
      
      <div className="security-features">
        <div className="security-feature">
          <i className="fas fa-lock"></i>
          <span>Secure Authentication</span>
        </div>
        <div className="security-feature">
          <i className="fas fa-certificate"></i>
          <span>Verified Account</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="form-container auth-security-container">
        

        {registerStep === 'form' && (
          <>
            <div className="auth-header">
              <h2>Create Your Secure Account</h2>
             
            </div>
            
            {/* Step Progress Indicator */}
            <div className="step-progress">
              <div className={`step-item ${step >= 1 ? (step > 1 ? 'completed' : 'active') : ''}`}>
                <div className={`step-circle ${step > 1 ? 'completed' : step === 1 ? 'active' : 'pending'}`}>
                  {step > 1 ? <i className="fas fa-check"></i> : <i className="fas fa-envelope"></i>}
                </div>
                <span className="step-label">Email</span>
              </div>
              
              <div className={`step-item ${step >= 2 ? (step > 2 ? 'completed' : 'active') : ''}`}>
                <div className={`step-circle ${step > 2 ? 'completed' : step === 2 ? 'active' : 'pending'}`}>
                  {step > 2 ? <i className="fas fa-check"></i> : <i className="fas fa-key"></i>}
                </div>
                <span className="step-label">Verify</span>
              </div>
              
              <div className={`step-item ${step >= 3 ? 'active' : ''}`}>
                <div className={`step-circle ${step === 3 ? 'active' : 'pending'}`}>
                  <i className="fas fa-user"></i>
                </div>
                <span className="step-label">Details</span>
              </div>
            </div>
          
            {error && (
              <div className="alert alert-error">
                <i className="fas fa-exclamation-triangle"></i>
                <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                <i className="fas fa-check-circle"></i>
                {success}
              </div>
            )}

            {renderStep()}

            <div className="auth-links">
              <Link to="/login" className="auth-link primary">
                Already have an account? <span>Sign In</span>
              </Link>
            </div>
          </>
        )}

        {registerStep === 'processing' && renderProcessing()}
        {registerStep === 'success' && renderRegistrationSuccess()}
      </div>
    </div>
  );
};

export default Register;